import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { Theme } from 'src/app/models/theme.model';
import { UtilService } from '../util/util.service';
import { StorageService } from '../storage/storage.service';
import { AppUser } from 'src/app/models/appUser.model';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Platform } from '@ionic/angular';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { AppInfoService } from '../appInfo/app-info.service';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private themeSubject = new BehaviorSubject<(Theme|null)>(null);
  theme = this.themeSubject.asObservable();
  timeThemeLoaded: number = 0; // Timestamp of when the theme was last set

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private utilService: UtilService,
    public platform: Platform,
    private settings: AppSettingsService,
    private appInfoService: AppInfoService
  ) { }

  userTheme(appUser: AppUser, timePageLoaded: number) {
    // if app is not 1Rink, always set user theme to default theme
    if (this.appInfoService.appID != environment.DEFUALT_APP_ID) {
      this.getDefaultTheme().subscribe(theme => {
        if(theme) {
          this.setTheme(theme, timePageLoaded);
        }
      });
    } else {
      if(appUser.themeID) { // User has a preferred theme saved
        this.getThemeByID(appUser.themeID).subscribe(theme => {
          if(theme) {
            this.setTheme(theme, timePageLoaded);
          }
        });
      } else { // User has no preferred theme saved
        this.storage.get("theme").then(theme => {
          if(theme) { // Theme saved locally
            this.setTheme(theme, timePageLoaded);
          } else { // Theme not saved locally
            this.getDefaultTheme().subscribe(theme => {
              this.setTheme(theme, timePageLoaded);
            });
          }
        });
      }
    }
  }

  setTheme(theme: Theme, timePageLoaded: number, setTemp?: boolean) {
    if (timePageLoaded < this.timeThemeLoaded) {
      return; // Don't set the theme if it was already set with a newer timestamp
    }
    const body = document.querySelector('body');
    if (theme.primaryColor != null && theme.primaryColorContrast != null) {
      body?.style.setProperty('--ion-color-primary', this.utilService.convertLongToHexColor(theme.primaryColor));
      body?.style.setProperty('--ion-color-primary-contrast', this.utilService.convertLongToHexColor(theme.primaryColorContrast));

      if (this.platform.is('cordova')) {
        let hexColor = this.utilService.convertLongToHexColor(theme.primaryColor);
        let colorMode = this.utilService.isLightOrDark(hexColor);
  
        if (colorMode == "light") {
          StatusBar.setStyle({style: Style.Light});
        } else if (colorMode == "dark") {
          StatusBar.setStyle({style: Style.Dark});
        }
      }
    }
    if (theme.secondaryColor != null && theme.secondaryColorContrast != null) {
      body?.style.setProperty('--ion-color-secondary', this.utilService.convertLongToHexColor(theme.secondaryColor));
      body?.style.setProperty('--ion-color-secondary-contrast', this.utilService.convertLongToHexColor(theme.secondaryColorContrast));
    }
    if (theme.thirdColor != null && theme.thirdColorContrast != null) {
      body?.style.setProperty('--ion-color-tertiary', this.utilService.convertLongToHexColor(theme.thirdColor));
      body?.style.setProperty('--ion-color-tertiary-contrast', this.utilService.convertLongToHexColor(theme.thirdColorContrast));
    }
    if (theme.backgroundColor != null && theme.backgroundColorContrast != null) {
      body?.style.setProperty('--ion-background-color', this.utilService.convertLongToHexColor(theme.backgroundColor));
      body?.style.setProperty('--ion-background-contrast-color', this.utilService.convertLongToHexColor(theme.backgroundColorContrast));
    }
    if (theme.offsetColor != null) {
      body?.style.setProperty('--ion-color-offset', this.utilService.convertLongToHexColor(theme.offsetColor));
      body?.style.setProperty('--ion-text-color', this.utilService.convertLongToHexColor(theme.offsetColor));
    }

    if(!setTemp) {
      this.storage.set("theme", theme);
    }

    this.timeThemeLoaded = timePageLoaded; // Update the theme loaded timestamp
    this.themeSubject.next(theme);
  }

  setThemeByID(themeID: number) {
    this.getThemeByID(themeID).subscribe((theme: Theme) => {
      this.setTheme(theme, moment().valueOf());
    });
  }

  getThemeByID(themeID: number): Observable<Theme> {
    const url = this.settings.apiUrl + `themes/${themeID}`;

    return this.http.get(url).pipe(
      map((data: any[]) => {
        return new Theme(data);
      })
    );
  }

  getDefaultThemes(): Observable<Theme[]> {
    const url = this.settings.apiUrl + `defaultThemes`;

    return this.http.get(url).pipe(
      map((data: any[]) => {
        let themes: Theme[] = [];
        data.forEach(theme => {
          themes.push(new Theme(theme));
        });
        return themes;
      })
    );
  }

  getDefaultTheme(): Observable<Theme> {
    const url = this.settings.apiUrl + `defaultTheme`;

    return this.http.get(url).pipe(
      map((data: any) => {
        return (new Theme(data));
      })
    );
  }

  updateUsersTheme(themeID: number): Observable<Theme> {
    const url = this.settings.apiUrl + `themes/${themeID}`;

    return this.http.put(url, {}).pipe(
      map((data: any) => {
        return new Theme(data);
      })
    );
  }

  setAlternateTheme(theme: Theme) {
    return {
      'color': this.utilService.convertLongToHexColor(theme.primaryColorContrast),
      'background': this.utilService.convertLongToHexColor(theme.primaryColor),
    };
  }
}
